import Dialog from "@mui/material/Dialog/Dialog";
import { theme as defaultTheme, Icon, Spinner } from "@mindlercare/ui-web";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { isErrorWithCode } from "api/helpers";
import { IcbtTranslationProvider, ProgramPages } from "@mindlercare/icbt";
import { Questionnaire } from "components/icbt/programs/ContentfulRichText/Questionnaire";
import { useIcbtTranslations } from "components/icbt/hooks/useIcbtTranslations";
import {
  CloseDialog,
  CloseIcon,
  DialogHeaderTextContainer,
  StyledDialogTitle,
} from "components/lib/customModal/customModal.styles";
import { Typography } from "components/typography";
import { useGetProgramContentQuery } from "hooks/icbt/useGetProgramContentQuery";

const ModifiedDialog = styled(Dialog)`
  margin: auto auto;
  height: 95vh;
  width: 75vw;
  @media (width < ${defaultTheme.BREAKPOINTS.XL}) {
    width: 95vw;
  }
  .MuiDialog-paperScrollPaper {
    height: 100%;
    width: 100%;
    padding: 0 40px 24px 40px;
  }
`;

export const DialogHeaderContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  color: ${(p) => defaultTheme.COLORS.GRAY_BLACK};
  font-size: ${(p) => defaultTheme.FONT.SIZE.LARGE};
  font-weight: ${(p) => defaultTheme.FONT.WEIGHT.MEDIUM};
  padding: 24px 5px;
`; // 5px so header matches the page padding

export const ScrollableWrapper = styled.div`
  overflow-y: auto;
`;
export const DialogTitle = styled.div`
  padding: 0px 0px;
`;

export const CenteredWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

export const PreviewProgramModal = ({
  handleCloseProgramPreviewModal,
  open,
  programId,
  languageCode,
}: {
  handleCloseProgramPreviewModal: () => void;
  open: boolean;
  programId: string;
  languageCode?: string;
}) => {
  const { t } = useTranslation();
  const icbtTranslations = useIcbtTranslations();
  const { data, isLoading, isFetching, isSuccess, error } =
    useGetProgramContentQuery({
      programId,
      languageCode,
    });

  return (
    <ModifiedDialog onClose={handleCloseProgramPreviewModal} open={open}>
      {(() => {
        if (!programId) return null;
        if (isLoading || isFetching)
          return (
            <CenteredWrapper>
              <Spinner />
            </CenteredWrapper>
          );
        if (!isSuccess || error) {
          return (
            <Typography variant="h3">
              {isErrorWithCode(error, 403)
                ? t("ICBT_LOADING_DATA_ERROR_FORBIDDEN")
                : t("ICBT_LOADING_DATA_ERROR")}
            </Typography>
          );
        }
        return (
          <>
            <StyledDialogTitle id="dialog-title">
              <DialogHeaderContainer>
                <DialogHeaderTextContainer>
                  <Typography variant="h3" data-testid="slot-dialog-title-text">
                    {data.title}
                  </Typography>
                </DialogHeaderTextContainer>

                <CloseDialog>
                  <CloseIcon onClick={handleCloseProgramPreviewModal}>
                    <Icon.Close data-testid="customModalCloseIcon" />
                  </CloseIcon>
                </CloseDialog>
              </DialogHeaderContainer>
            </StyledDialogTitle>
            <ScrollableWrapper>
              <IcbtTranslationProvider translations={icbtTranslations}>
                <ProgramPages
                  pages={data.pages}
                  overrideMaxWidth="min(75wv, 768px)"
                  renderQuestionnaire={(entry) => (
                    <Questionnaire
                      key={entry.sys.id}
                      questionnaireType="icbt"
                      clinicalId={entry.clinicalId}
                    />
                  )}
                />
              </IcbtTranslationProvider>
            </ScrollableWrapper>
          </>
        );
      })()}
    </ModifiedDialog>
  );
};
